"use client";

import { refreshAccessToken, checkUserApiKeyManager } from "@/app/utils/auth";
import React, {createContext, useContext, useEffect} from "react";

export interface AuthContextProps {
    checkIsUserAuthenticated: () => Promise<boolean>;
    checkIsUserApiKeyManager: () => Promise<boolean>;
    login: () => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    useEffect(() => {
        initializeAuth();

        const handleStorageChange = () => {
            initializeAuth();
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        }
    }, []);

    const checkIsUserAuthenticated = async (): Promise<boolean> => {
        let token = localStorage.getItem("authToken");
        let idToken = localStorage.getItem("idToken");
        const expiryTime = parseInt(localStorage.getItem("tokenExpiry") || "0", 10);
        if (Date.now() >= expiryTime) {
            await refreshAccessToken();
            token = localStorage.getItem("authToken");
            idToken = localStorage.getItem("idToken");
        }
        if (token && idToken) {
            return true;
        } else {
            return false;
        }
    }

    const checkIsUserApiKeyManager = () => {
        const data = localStorage.getItem("isUserApiKeyManager");
        if (data === "true") {
            return Promise.resolve(true);
        } else if (data === "false") {
            return Promise.resolve(false);
        } else {
            return checkUserApiKeyManager();
        }
    }


    const initializeAuth = () => {
        const token = localStorage.getItem("authToken");
        const idToken = localStorage.getItem("idToken");
        const expiryTime = parseInt(localStorage.getItem("tokenExpiry") || "0", 10);
        
        if (Date.now() >= expiryTime && localStorage.getItem("refreshToken")) {
            refreshAccessToken();
        } else if (token && idToken) {
            return;
        } else {
            clearStorage();
        }
    };

    const login = () => {
        import("@/app/utils/auth").then((module) => module.handleLogin());
    };

    const logout = () => {
        import("@/app/utils/auth").then((module) => module.handleLogout());
    };

    const clearStorage = () => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiry");
        localStorage.removeItem("idToken");

        localStorage.removeItem("codeVerifier");
        localStorage.removeItem("oauthState");
        localStorage.removeItem("isUserApiKeyManager");
    };

    return (
        <AuthContext.Provider value = {{checkIsUserAuthenticated, login, logout, checkIsUserApiKeyManager}}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if(!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};